import type { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import {
  Button,
  SectionContent,
  SectionGroup,
  SectionHeader,
  SectionItem,
  SectionItemValue,
  SectionTitle,
} from '@/components/ui'
import { TermosDaProposta } from '@/features/proposta/shared/components/termos-da-proposta'

type TDetalhesPropostaResumoSectionProps = {
  assinaturaContrato: string
  termosDaProposta: string
  observacoes?: string | null
  onEdit: (step: number) => void
  stepDetalhes?: number
  /** Conteúdo extra renderizado antes do bloco de assinatura (ex.: participações de imobiliária no Brasil). */
  children?: ReactNode
}

const STEP_DETALHES_PROPOSTA = 2

export function DetalhesPropostaResumoSection({
  assinaturaContrato,
  termosDaProposta,
  observacoes,
  onEdit,
  stepDetalhes = STEP_DETALHES_PROPOSTA,
  children,
}: Readonly<TDetalhesPropostaResumoSectionProps>) {
  const { t } = useTranslation('proposta')
  const modeloAssinatura =
    assinaturaContrato === 'Eletronica'
      ? t('detalhes_proposta.assinaturas.options.eletronica')
      : t('detalhes_proposta.assinaturas.options.impressa')

  return (
    <div>
      <SectionHeader>
        <SectionTitle>{t('resumo_proposta.detalhes_proposta.titulo')}</SectionTitle>
        <div className="hidden sm:block">
          <Button
            variant="ghost"
            className="h-8 text-sky-700 hover:bg-neutral-200 hover:text-sky-900"
            onClick={() => onEdit(stepDetalhes)}
          >
            <Icon name="pencil" className="mr-1 size-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </SectionHeader>
      <SectionContent>
        {children}

        <div className="space-y-3">
          <h3 className="font-poppins text-base font-normal uppercase">
            {t('resumo_proposta.detalhes_proposta.label.assinatura')}
          </h3>
          <SectionGroup withBorder className="border-neutral-300">
            <SectionItem label={t('resumo_proposta.detalhes_proposta.label.modelo_assinatura')}>
              <SectionItemValue>{modeloAssinatura}</SectionItemValue>
            </SectionItem>
          </SectionGroup>
        </div>

        <div className="space-y-3">
          <h3 className="font-poppins text-base font-normal uppercase">
            {t('resumo_proposta.detalhes_proposta.label.termos_da_proposta')}
          </h3>
          <TermosDaProposta className="text-sm text-neutral-800" termos={termosDaProposta} assinaturaContrato="-" />
        </div>

        <Render.If condition={!!observacoes}>
          {() => (
            <div className="space-y-3 border-t border-neutral-300 pt-3">
              <h3 className="text-base font-normal uppercase">
                {t('resumo_proposta.detalhes_proposta.label.observacoes')}
              </h3>
              <ul className="ml-2 list-disc space-y-4 pl-5">
                <li className="text-sm break-words whitespace-pre-wrap text-neutral-800">{observacoes}</li>
              </ul>
            </div>
          )}
        </Render.If>

        <div className="block sm:hidden">
          <Button variant="outline" className="w-full" onClick={() => onEdit(stepDetalhes)}>
            <Icon name="pencil" className="mr-1 size-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </SectionContent>
    </div>
  )
}
